import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  /**
   * Reads the variant id for a cart line from the rendered quantity selector.
   * @param {number} line - The 1-based cart line number.
   * @returns {string|null} The variant id for the line.
   */
  #readVariantIdForLine(line) {
    const lineItemRow = this.refs.cartItemRows[line - 1];
    if (!(lineItemRow instanceof HTMLElement)) return null;

    const quantitySelector = lineItemRow.querySelector('cart-quantity-selector-component, quantity-selector-component');
    if (!(quantitySelector instanceof HTMLElement)) return null;

    return quantitySelector.dataset.variantId || null;
  }

  /**
   * Reads the key for a cart line from parent row.
   * @param {number} line - The 1-based cart line number.
   * @returns {string|null} The variant id for the line.
   */
  #readKeyForLine(line) {
    const lineItemRow = this.refs.cartItemRows[line - 1];
    if (!(lineItemRow instanceof HTMLElement)) return null;

    return lineItemRow.dataset.key || null;
  }

  /**
   * Parses line item properties from parent row.
   * @param {number} line - The 1-based cart line number.
   * @returns {object|null} JSON object of line item properties.
   */
    #readLineItemProperties(line) {
      const lineItemRow = this.refs.cartItemRows[line - 1];
      if (!(lineItemRow instanceof HTMLElement)) return null;
      const scriptEl = lineItemRow.querySelector('script[type="application/json"][data-content="line-item-properties"]');
      if (!(scriptEl instanceof HTMLScriptElement)) return null;
      // @ts-ignore
      return window.offerEngine?.parseLiquidJsonArrayContent?.(JSON.parse(scriptEl.textContent)) || null;
  
    }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityChange);
  }

  #handleQuantityChange = (event) => {
    if (!(event.target instanceof Node) || !this.contains(event.target) || !event.detail.cartLine) return;

    document.querySelectorAll('input[name="preorder-toggle"]').forEach((toggle) => {
      toggle.disabled = true;
    });
    this.#debouncedOnChange(event);
  };

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Handles the line item selling plan toggle.
   * @param {number} line - The line item index.
   * @param {Event} event - The change event.
   */
  onLineItemSellingPlanToggle(line, event) {
    if (!(event.target instanceof HTMLInputElement)) return;

    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');
    const quantity = quantityInput instanceof HTMLInputElement
      ? parseInt(quantityInput.value, 10) || 1
      : 1;

    this.updateQuantity({
      line,
      quantity,
      action: 'subscription-toggle',
      sellingPlan: event.target.checked ? event.target.dataset.sellingPlanId || '' : '',
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   * @param {string} [config.sellingPlan] - The selling plan id to apply, or blank to remove.
   */
  async updateQuantity(config) {
    document.dispatchEvent(new CustomEvent('cart:line-quantity-update', { detail: config }));
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);
    const variantId = this.#readVariantIdForLine(config.line);
    const key = this.#readKeyForLine(config.line);
    const properties = this.#readLineItemProperties(config.line);

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    this.#disableCartItems();
    cartTotal?.shimmer();

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId, 'offer-engine-json']);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const payload = {
      line: line,
      key: key,
      variant_id: variantId,
      quantity: quantity,
      properties: properties,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    };
    if (config.sellingPlan !== undefined) {
      payload.selling_plan = config.sellingPlan;
    }

    const formData = new FormData();
    formData.set('line', line);
    formData.set('key', key);
    formData.set('variant_id', variantId || '');
    formData.set('quantity', quantity);
    formData.set('sections', payload.sections);
    formData.set('sections_url', payload.sections_url);

    Object.entries(properties || {}).forEach(([key, value]) => {
      formData.set(`properties[${key}]`, value);
    });

    if (config.sellingPlan !== undefined) {
      formData.set('selling_plan', config.sellingPlan);
    }

    if (window.offerEngine?.parseChangePayloadData) {
      try {
        const offerHandled = await window.offerEngine.parseChangePayloadData(null, formData);
        if (offerHandled === true) {
          this.#enableCartItems();
          cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
          return;
        }
      } catch (error) {
        console.error(error);
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
        return;
      }
    }

    const cartMutation = fetch(
      `${Theme.routes.cart_change_url}`,
      fetchConfig('json', { body: JSON.stringify(payload) })
    )
      .then((response) => response.text())
      .then((responseText) => JSON.parse(responseText));
          
    Promise.resolve(cartMutation).then((parsedResponseText) => {

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const sectionHtml = parsedResponseText.sections?.[this.sectionId];
        if (!sectionHtml) {
          sectionRenderer.renderSection(this.sectionId, { cache: false });
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          sectionHtml,
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            variantId,
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, sectionHtml);

        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
    // Prevent preorder changes while the quantity mutation is pending.
    document.querySelectorAll('input[name="preorder-toggle"]').forEach((toggle) => {
      toggle.disabled = true;
    });
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
    document.querySelectorAll('input[name="preorder-toggle"]').forEach((toggle) => {
      toggle.disabled = false;
    });
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
